import React, { useState, useEffect } from 'react';
import {
  ClipboardList,
  Check,
  AlertCircle,
  Dumbbell,
  Send,
  X,
  Loader2,
} from 'lucide-react';
import type { SurveyField, SurveyTemplate } from '../types';
import { useSurveyStore } from '../store/useSurveyStore';
import { useAuthStore } from '../store/useAuthStore';

const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

interface Props {
  /** May be either a MongoDB _id (preview/trainer view) or a publicToken (public hash URL) */
  surveyId: string;
  isPreview?: boolean;
}

export default function SurveyFillPage({ surveyId, isPreview }: Props) {
  // Try store first (covers preview & trainer view where surveys are already loaded)
  const storeSurvey = useSurveyStore((s) =>
    s.surveys.find((sv) => sv.id === surveyId || sv.publicToken === surveyId)
  );
  const submitResponse = useSurveyStore((s) => s.submitResponse);
  const profiles = useAuthStore((s) => s.profiles) || [];

  // For public visitors the store is empty - fetch from API using the public token
  const [fetchedSurvey, setFetchedSurvey] = useState<SurveyTemplate | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch if not already found in the store
    if (storeSurvey) return;
    setFetchLoading(true);
    setFetchError(null);
    fetch(`${API_BASE_URL}/surveys/public/${surveyId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Nie znaleziono ankiety');
        return res.json();
      })
      .then((data) => {
        const s = data.survey;
        setFetchedSurvey({
          ...s,
          id: s._id || s.id,
          publicToken: s.publicToken,
          trainerId: s.ownerId || s.trainerId || '',
        });
      })
      .catch((err) => setFetchError(err.message || 'Błąd pobierania ankiety'))
      .finally(() => setFetchLoading(false));
  }, [surveyId, storeSurvey]);

  const survey = storeSurvey ?? fetchedSurvey;

  const [answers, setAnswers] = useState<Record<string, string | string[] | number | boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize defaults
  useEffect(() => {
    if (!survey) return;
    const defaults: Record<string, string | string[] | number | boolean> = {};
    for (const f of survey.fields) {
      if (f.type === 'checkbox') defaults[f.id] = false;
      else if (f.type === 'multiselect') defaults[f.id] = [];
      else if (f.type === 'range') defaults[f.id] = f.defaultValue || String(f.min ?? 0);
      else defaults[f.id] = '';
    }
    setAnswers(defaults);
  }, [survey]);

  if (fetchLoading) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 animate-fade-in">
        <Loader2 size={40} className="mx-auto text-brand-600 animate-spin mb-4" />
        <p className="text-sm text-slate-500">Ładowanie ankiety...</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 animate-fade-in">
        <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-bold text-slate-500 dark:text-slate-400 mb-1">
          {fetchError ?? 'Ankieta nie istnieje'}
        </h2>
        <p className="text-sm text-slate-400">
          Sprawdź czy link jest poprawny lub skontaktuj się z trenerem.
        </p>
      </div>
    );
  }


  // Find trainer name
  const trainer = profiles && Array.isArray(profiles) ? profiles.find((p) => p.id === survey.trainerId) : undefined;

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-950 mb-4">
          <Check size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold mb-2">Dziękujemy!</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Twoja ankieta została wysłana. Trener skontaktuje się z Tobą wkrótce.
        </p>
      </div>
    );
  }

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    for (const field of survey.fields) {
      if (!field.required) continue;
      const val = answers[field.id];
      if (val === undefined || val === '' || val === null) {
        errs[field.id] = 'To pole jest wymagane';
      } else if (Array.isArray(val) && val.length === 0) {
        errs[field.id] = 'Wybierz co najmniej jedną opcję';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (isPreview) {
      alert('To jest podgląd - odpowiedź nie zostanie zapisana.');
      return;
    }
    if (!validate()) return;

    // Get respondent name from answers (first text field, or first field)
    const nameField = survey.fields.find(
      (f) => f.type === 'text' && f.label.toLowerCase().includes('imi')
    );
    const respondentName = nameField
      ? String(answers[nameField.id] || 'Anonimowy')
      : 'Anonimowy';

    const emailField = survey.fields.find(
      (f) => f.type === 'text' && f.label.toLowerCase().includes('mail')
    );

    submitResponse({
      surveyId: survey.id,
      trainerId: survey.trainerId,
      respondentName,
      respondentEmail: emailField ? String(answers[emailField.id] || '') : undefined,
      answers,
      publicToken: survey.publicToken,
    });
    setSubmitted(true);
  };

  const updateAnswer = (fieldId: string, value: string | string[] | number | boolean) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) setErrors((prev) => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  const toggleMultiselect = (fieldId: string, option: string) => {
    const current = (answers[fieldId] as string[]) || [];
    const next = current.includes(option)
      ? current.filter((o) => o !== option)
      : [...current, option];
    updateAnswer(fieldId, next);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-100 dark:bg-brand-950 mb-4">
          <Dumbbell size={28} className="text-brand-600" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight mb-1">{survey.title}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{survey.description}</p>
        {trainer && (
          <p className="text-xs text-slate-400 mt-2">Trener: {trainer.name}</p>
        )}
        {isPreview && (
          <div className="mt-3 inline-block px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 text-xs font-semibold">
            PODGLĄD - odpowiedzi nie będą zapisane
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="space-y-5">
        {survey.fields.map((field, idx) => (
          <fieldset
            key={field.id}
            className={`p-4 rounded-xl border transition ${errors[field.id]
              ? 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-950/20'
              : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
              }`}
          >
            <legend className="text-sm font-semibold px-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </legend>

            <div className="mt-2">
              {renderField(field, answers[field.id], updateAnswer, toggleMultiselect)}
            </div>

            {errors[field.id] && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle size={11} /> {errors[field.id]}
              </p>
            )}
          </fieldset>
        ))}
      </div>

      {/* Submit */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleSubmit}
          className="flex items-center gap-2 px-8 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition-colors shadow-lg shadow-brand-200 dark:shadow-brand-950"
        >
          <Send size={18} />
          {isPreview ? 'Wyślij (podgląd)' : 'Wyślij ankietę'}
        </button>
      </div>
    </div>
  );
}

// ── Field renderer ──────────────────────────

function renderField(
  field: SurveyField,
  value: string | string[] | number | boolean | undefined,
  onChange: (id: string, val: string | string[] | number | boolean) => void,
  toggleMulti: (id: string, option: string) => void,
) {
  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition';

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={(value as string) || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          placeholder={field.placeholder}
          className={inputClass}
        />
      );

    case 'textarea':
      return (
        <textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={`${inputClass} resize-y`}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={value !== undefined && value !== '' ? String(value) : ''}
          onChange={(e) => onChange(field.id, e.target.value ? Number(e.target.value) : '')}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          className={inputClass}
        />
      );

    case 'select':
      return (
        <select
          value={(value as string) || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          className={inputClass}
        >
          <option value="">Wybierz...</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );

    case 'multiselect': {
      const isEquipmentField = field.label.toLowerCase().includes('sprzęt') || field.label.toLowerCase().includes('equip');
      return (
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((opt) => {
            const selected = Array.isArray(value) && value.includes(opt);
            const icon = isEquipmentField ? getEquipmentIcon(opt) : null;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggleMulti(field.id, opt)}
                className={`px-3 py-2 rounded-lg text-xs font-medium border transition flex items-center gap-2 ${selected
                  ? 'bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 border-brand-400 dark:border-brand-500 shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-brand-300'
                  }`}
              >
                {icon && <span className="flex-shrink-0">{icon}</span>}
                {selected && !icon && <Check size={10} className="inline -mt-0.5" />}
                {opt}
              </button>
            );
          })}
        </div>
      );
    }

    case 'radio':
      return (
        <div className="space-y-2">
          {(field.options || []).map((opt) => (
            <label
              key={opt}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${value === opt
                ? 'bg-brand-50 dark:bg-brand-950 border-brand-400 dark:border-brand-500'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                }`}
            >
              <input
                type="radio"
                name={field.id}
                checked={value === opt}
                onChange={() => onChange(field.id, opt)}
                className="accent-brand-500"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(field.id, e.target.checked)}
            className="accent-brand-500 w-4 h-4"
          />
          <span className="text-sm">Tak</span>
        </label>
      );

    case 'range': {
      const min = field.min ?? 0;
      const max = field.max ?? 100;
      const step = field.step ?? 1;
      const current = value !== undefined && value !== '' ? Number(value) : Number(field.defaultValue || min);
      return (
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>{min}</span>
            <span className="font-semibold text-brand-600 dark:text-brand-400 text-sm">{current}</span>
            <span>{max}</span>
          </div>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={current}
            onChange={(e) => onChange(field.id, Number(e.target.value))}
            className="w-full accent-brand-500"
          />
        </div>
      );
    }

    default:
      return null;
  }
}

// ── Equipment Icons (inline SVG) ────────────

function getEquipmentIcon(option: string): React.ReactNode | null {
  const lower = option.toLowerCase();
  const sz = 20;
  const cls = 'inline-block';

  // Full gym
  if (lower.includes('pełna') || lower.includes('komercyjna')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <rect x="2" y="10" width="4" height="4" rx="0.5" />
        <rect x="18" y="10" width="4" height="4" rx="0.5" />
        <rect x="6" y="8" width="2" height="8" rx="0.5" />
        <rect x="16" y="8" width="2" height="8" rx="0.5" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    );
  }

  // Home gym
  if (lower.includes('domowa')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <path d="M3 12l9-8 9 8" />
        <path d="M5 10v10h14V10" />
        <line x1="9" y1="17" x2="15" y2="17" />
        <rect x="8" y="15" width="2" height="4" rx="0.5" />
        <rect x="14" y="15" width="2" height="4" rx="0.5" />
      </svg>
    );
  }

  // Barbell / Sztanga
  if (lower.includes('sztanga') || lower.includes('barbell')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <rect x="1" y="9" width="3" height="6" rx="0.5" />
        <rect x="20" y="9" width="3" height="6" rx="0.5" />
        <rect x="4" y="7" width="2" height="10" rx="0.5" />
        <rect x="18" y="7" width="2" height="10" rx="0.5" />
        <line x1="6" y1="12" x2="18" y2="12" />
      </svg>
    );
  }

  // Dumbbells / Hantle
  if (lower.includes('hantl') || lower.includes('dumbbell')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <rect x="2" y="9" width="3" height="6" rx="1" />
        <rect x="19" y="9" width="3" height="6" rx="1" />
        <rect x="5" y="10" width="2" height="4" rx="0.5" />
        <rect x="17" y="10" width="2" height="4" rx="0.5" />
        <line x1="7" y1="12" x2="17" y2="12" />
      </svg>
    );
  }

  // Kettlebell
  if (lower.includes('kettlebell') || lower.includes('kettle')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <circle cx="12" cy="15" r="5" />
        <path d="M9 8a4 4 0 0 1 6 0" />
        <line x1="9" y1="10" x2="9" y2="8" />
        <line x1="15" y1="10" x2="15" y2="8" />
      </svg>
    );
  }

  // Resistance bands / Gumy
  if (lower.includes('gum') || lower.includes('band')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <ellipse cx="12" cy="12" rx="8" ry="4" />
        <path d="M4 12c0 2 2.5 5 8 5s8-3 8-5" strokeDasharray="3 2" />
      </svg>
    );
  }

  // Machines / Maszyny
  if (lower.includes('maszyn') || lower.includes('machine')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <rect x="4" y="2" width="4" height="20" rx="1" />
        <rect x="16" y="8" width="4" height="8" rx="1" />
        <line x1="8" y1="6" x2="16" y2="10" />
        <line x1="8" y1="18" x2="16" y2="14" />
        <circle cx="6" cy="6" r="1" fill="currentColor" />
        <circle cx="6" cy="18" r="1" fill="currentColor" />
      </svg>
    );
  }

  // Cable / Wyciąg
  if (lower.includes('wyciąg') || lower.includes('cable')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <rect x="10" y="2" width="4" height="4" rx="1" />
        <line x1="12" y1="6" x2="12" y2="16" strokeDasharray="2 2" />
        <circle cx="12" cy="18" r="2" />
        <line x1="6" y1="22" x2="18" y2="22" />
        <line x1="6" y1="2" x2="6" y2="22" />
        <line x1="18" y1="2" x2="18" y2="22" />
      </svg>
    );
  }

  // Bodyweight / Masa ciała / Brak
  if (lower.includes('masa ciała') || lower.includes('brak') || lower.includes('bodyweight')) {
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
        <circle cx="12" cy="5" r="3" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="11" x2="16" y2="11" />
        <line x1="12" y1="16" x2="8" y2="22" />
        <line x1="12" y1="16" x2="16" y2="22" />
      </svg>
    );
  }

  return null;
}
